import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCafe } from '../context/CafeContext';
import { useLanguage } from '../context/LanguageContext';
import PaymentModal from '../components/PaymentModal';
import BillModal from '../components/BillModal';
import CompletedSales from '../components/CompletedSales';
import { Trash2, ShoppingBag, DollarSign, Clock, Hash, CreditCard, Plus, CheckSquare, Square, Printer } from 'lucide-react';
import { showNotification } from '../components/GlobalNotification';
import { getItemImageSrc } from '../utils/itemImage';
import { orderLabel } from '../utils/itemName';
import api from '../config/api';

function Orders() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const {
    activeOrders,
    bulkCompleteOrders,
    removeOrder,
    refreshActiveOrders,
    refreshItems,
    currentToken,
    selectToken,
    clearCurrentToken,
    loading,
    error
  } = useCafe();

  const [view, setView] = useState('active');
  const [payingToken, setPayingToken] = useState(null);
  // Tokens ticked to be settled together. A table that ordered in two rounds
  // gets two tokens and pays once, which is what the bulk endpoint is for.
  const [selectedTokens, setSelectedTokens] = useState([]);
  const [payingSelection, setPayingSelection] = useState(false);
  // Which token's items are on screen. Tokens are listed as a horizontal strip
  // so a counter with a dozen open bills stays one tap away from any of them.
  const [openToken, setOpenToken] = useState(null);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [completedLoading, setCompletedLoading] = useState(false);
  // Billing is reached two ways: the header button, where the token still has
  // to be typed, and a token's own Bill button, which fills it in already
  const [billOpen, setBillOpen] = useState(false);
  const [billToken, setBillToken] = useState(null);

  const openBill = (tokenNumber = null) => {
    setBillToken(tokenNumber);
    setBillOpen(true);
  };

  // Orders are told apart by the token handed to the customer, so the list is
  // grouped by token and each token is paid as one bill
  const tokenGroups = useMemo(() => {
    const groups = new Map();

    (activeOrders || []).forEach((order) => {
      const token = order.tokenNumber || 0;
      if (!groups.has(token)) {
        groups.set(token, []);
      }
      groups.get(token).push(order);
    });

    return Array.from(groups.entries())
      .map(([tokenNumber, orders]) => ({
        tokenNumber,
        orders,
        total: orders.reduce((sum, order) => sum + (order.totalAmount || 0), 0),
        startedAt: orders.reduce(
          (earliest, order) =>
            !earliest || new Date(order.createdAt) < new Date(earliest) ? order.createdAt : earliest,
          null
        )
      }))
      .sort((a, b) => a.tokenNumber - b.tokenNumber);
  }, [activeOrders]);

  const payingGroup = tokenGroups.find((group) => group.tokenNumber === payingToken) || null;
  const openGroup = tokenGroups.find((group) => group.tokenNumber === openToken) || null;
  const totalPendingValue = tokenGroups.reduce((sum, group) => sum + group.total, 0);

  // Tokens can be paid one at a time or several as one bill. Both end up in the
  // same payment modal, so the two are resolved to one shape here.
  const selectedGroups = tokenGroups.filter((group) => selectedTokens.includes(group.tokenNumber));
  const selectedTotal = selectedGroups.reduce((sum, group) => sum + group.total, 0);

  const paying = payingSelection
    ? {
        tokens: selectedGroups.map((group) => group.tokenNumber),
        orders: selectedGroups.flatMap((group) => group.orders),
        label:
          selectedGroups.length === 1
            ? t('Token #{n}', { n: selectedGroups[0].tokenNumber })
            : t('{count} tokens', { count: selectedGroups.length })
      }
    : payingGroup
      ? {
          tokens: [payingGroup.tokenNumber],
          orders: payingGroup.orders,
          label: t('Token #{n}', { n: payingGroup.tokenNumber })
        }
      : null;

  const toggleTokenSelection = (tokenNumber) => {
    setSelectedTokens((current) =>
      current.includes(tokenNumber)
        ? current.filter((value) => value !== tokenNumber)
        : [...current, tokenNumber]
    );
  };

  // A token that has been paid or cleared elsewhere must not stay ticked, or the
  // next combined payment would send order ids the server no longer has
  useEffect(() => {
    setSelectedTokens((current) => {
      const live = current.filter((tokenNumber) =>
        tokenGroups.some((group) => group.tokenNumber === tokenNumber)
      );
      return live.length === current.length ? current : live;
    });
  }, [tokenGroups]);

  // Keep a token open at all times: fall back to the one being rung up, or the
  // oldest still waiting, whenever the selected one is paid or removed
  useEffect(() => {
    if (tokenGroups.length === 0) {
      if (openToken !== null) {
        setOpenToken(null);
      }
      return;
    }

    if (tokenGroups.some((group) => group.tokenNumber === openToken)) {
      return;
    }

    const fallback = tokenGroups.find((group) => group.tokenNumber === currentToken) || tokenGroups[0];
    setOpenToken(fallback.tokenNumber);
  }, [tokenGroups, openToken, currentToken]);

  const fetchCompletedOrders = useCallback(async () => {
    try {
      setCompletedLoading(true);
      const response = await api.get('/api/orders/completed');
      setCompletedOrders(response.data);
    } catch (fetchError) {
      showNotification('error', t("Failed to load today's sales."));
    } finally {
      setCompletedLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (view === 'completed') {
      fetchCompletedOrders();
    }
  }, [view, fetchCompletedOrders]);

  const handleCompletePayment = async (paymentType, discountData, dueDetails) => {
    if (!paying) {
      return;
    }

    const tokens = paying.tokens;

    try {
      // A flat discount is split across the lines by value on the server, so it
      // is passed once for the whole bill rather than per token
      const result = await bulkCompleteOrders(
        paying.orders.map((order) => order._id),
        paymentType,
        discountData,
        dueDetails
      );

      // The endpoint reports per-order failures rather than failing the whole
      // request, so a partial success has to be shown, not swallowed
      const failures = result?.errors?.length || 0;

      if (failures > 0) {
        showNotification('warning', t('Paid, but {count} line(s) could not be settled.', { count: failures }));
      } else if (paymentType === 'due') {
        showNotification(
          'success',
          t('Put on account for {name}.', { name: dueDetails?.dueCustomerName || t('customer') })
        );
      } else {
        showNotification(
          'success',
          tokens.length === 1
            ? t('Token #{n} paid.', { n: tokens[0] })
            : t('{count} tokens paid as one bill.', { count: tokens.length })
        );
      }

      // If the customer we were still ringing up has now paid, drop the token
      // so the next order starts with a freshly created one
      if (tokens.includes(currentToken)) {
        clearCurrentToken();
      }

      setPayingToken(null);
      setPayingSelection(false);
      setSelectedTokens((current) => current.filter((value) => !tokens.includes(value)));
      await refreshActiveOrders();
      await refreshItems();
      window.dispatchEvent(new CustomEvent('revenueUpdate'));
    } catch (completeError) {
      showNotification('error', t('Failed to complete the order. Please try again.'));
    }
  };

  // A token stays open until it is paid, so the customer can order again on the
  // same number - this puts the cashier back on their bill
  const handleAddToToken = (tokenNumber) => {
    selectToken(tokenNumber);
    navigate('/');
  };

  const handleRemoveOrder = async (order) => {
    if (!window.confirm(t('Remove {name} from token #{n}?', { name: orderLabel(order, language), n: order.tokenNumber }))) {
      return;
    }

    try {
      await removeOrder(order._id);
      showNotification('success', t('Item removed.'));
      await refreshActiveOrders();
      await refreshItems();
    } catch (removeError) {
      showNotification('error', t('Failed to remove the item. Please try again.'));
    }
  };

  const handleVoidOrder = async (order) => {
    const amount = order.finalAmount ?? order.totalAmount ?? 0;

    if (!window.confirm(
      t("Void the sale of {name} for Rs {amount}? This removes it from today's revenue.", {
        name: orderLabel(order, language),
        amount: amount.toFixed(2)
      })
    )) {
      return;
    }

    try {
      await api.put(`/api/orders/${order._id}/void`);
      showNotification('success', t('Sale voided.'));
      await fetchCompletedOrders();
      // Stock comes back on a void, so the menu counts need refreshing
      await refreshItems();
      window.dispatchEvent(new CustomEvent('revenueUpdate'));
    } catch (voidError) {
      showNotification('error', voidError.response?.data?.message || t('Failed to void the sale.'));
    }
  };

  const completedTotal = completedOrders
    .filter((order) => order.status === 'completed')
    .reduce((sum, order) => sum + (order.finalAmount ?? order.totalAmount ?? 0), 0);

  const formatTime = (value) =>
    value ? new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

  const formatWaitTime = (createdAt) => {
    const minutes = Math.floor((Date.now() - new Date(createdAt)) / (1000 * 60));
    if (minutes < 60) return t('{n}m', { n: minutes });
    const hours = Math.floor(minutes / 60);
    return t('{h}h {m}m', { h: hours, m: minutes % 60 });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafe-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{t('Error: {message}', { message: error })}</p>
        <button
          onClick={refreshActiveOrders}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          {t('Try Again')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header - on mobile the tabs below double as the title, so the heading
          and its help text only appear once there is room for them */}
      <div className="hidden sm:flex sm:items-start sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {view === 'active' ? t('Open Tokens') : t("Today's Sales")}
          </h1>
          <p className="text-gray-600 mt-1">
            {view === 'active'
              ? t('Each token is one customer. Tap a token to see its items, add more, or take payment.')
              : t('Completed sales for today - void one to correct a mistake')}
          </p>
        </div>
        <button
          onClick={() => openBill()}
          className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
        >
          <Printer className="h-4 w-4" />
          {t('Bill')}
        </button>
      </div>

      {/* On a phone the heading above is hidden, so the button needs its own
          place - a customer asking for a bill is asking at the counter, and the
          counter is usually a phone */}
      <button
        onClick={() => openBill()}
        className="sm:hidden w-full flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
      >
        <Printer className="h-4 w-4" />
        {t('Print a Bill')}
      </button>

      {/* View Tabs - stay pinned under the app header on mobile so switching
          views never needs a scroll back to the top */}
      <div className="sticky top-16 z-30 -mx-4 px-4 py-2 bg-cafe-50 sm:static sm:mx-0 sm:px-0 sm:py-0 sm:bg-transparent">
        <div className="flex bg-gray-100 rounded-lg p-1 w-full sm:w-auto sm:inline-flex">
          {[
            { key: 'active', label: t('Open Tokens') },
            { key: 'completed', label: t("Today's Sales") }
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setView(key)}
              className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                view === key ? 'bg-white text-cafe-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {view === 'completed' ? (
        <CompletedSales
          orders={completedOrders}
          loading={completedLoading}
          total={completedTotal}
          onVoid={handleVoidOrder}
          formatTime={formatTime}
        />
      ) : (
        <>
          {/* Summary Cards - side by side on mobile so the tokens themselves
              stay above the fold */}
          <div className="grid grid-cols-2 gap-2 sm:gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg flex-shrink-0">
                  <Hash className="h-4 w-4 sm:h-6 sm:w-6 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] sm:text-sm font-medium text-gray-500 truncate">{t('Open Tokens')}</p>
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 leading-tight">
                    {tokenGroups.length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg flex-shrink-0">
                  <DollarSign className="h-4 w-4 sm:h-6 sm:w-6 text-green-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] sm:text-sm font-medium text-gray-500 truncate">{t('Unpaid')}</p>
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 leading-tight truncate">
                    Rs {totalPendingValue.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Token groups */}
          {tokenGroups.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 text-center">
              <ShoppingBag className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-1 sm:mb-2">{t('No open tokens')}</h3>
              <p className="text-sm sm:text-base text-gray-500 mb-4">
                {t('Add items from the dashboard to start an order.')}
              </p>
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 px-4 py-2.5 min-h-[44px] bg-cafe-600 text-white rounded-lg hover:bg-cafe-700 transition-colors font-medium"
              >
                <Plus className="h-4 w-4" />
                {t('New Order')}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Token strip - one tap switches which bill is on screen. Bleeds
                  to the screen edge on mobile so it reads as a scrollable row. */}
              <div className="-mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto no-scrollbar">
                <div className="flex gap-2 pb-1 min-w-max">
                  {tokenGroups.map((group) => {
                    const isOpen = group.tokenNumber === openToken;
                    const isSelected = selectedTokens.includes(group.tokenNumber);

                    return (
                      <div
                        key={`strip-${group.tokenNumber}`}
                        className={`flex-shrink-0 flex items-stretch rounded-xl border-2 overflow-hidden transition-colors ${
                          isOpen
                            ? 'bg-cafe-600 border-cafe-600 text-white shadow-sm'
                            : isSelected
                              ? 'bg-cafe-50 border-cafe-400 text-gray-700'
                              : 'bg-white border-gray-200 text-gray-700 hover:border-cafe-400'
                        }`}
                      >
                        <button
                          onClick={() => setOpenToken(group.tokenNumber)}
                          className="px-3 py-2 text-left"
                        >
                          <div className="flex items-center gap-1.5">
                            <span className="text-base sm:text-lg font-bold leading-none">
                              #{group.tokenNumber}
                            </span>
                            {group.tokenNumber === currentToken && (
                              <span
                                className={`h-2 w-2 rounded-full flex-shrink-0 ${
                                  isOpen ? 'bg-white' : 'bg-cafe-500'
                                }`}
                                title={t('In progress')}
                              />
                            )}
                          </div>
                          {/* Amount and item count share a line so the strip stays
                              short on a phone */}
                          <p
                            className={`text-[11px] mt-1 whitespace-nowrap font-medium ${
                              isOpen ? 'text-white' : 'text-gray-600'
                            }`}
                          >
                            Rs {group.total.toFixed(2)}
                            <span className={isOpen ? 'text-cafe-100' : 'text-gray-400'}>
                              {' · '}
                              {group.orders.length}
                            </span>
                          </p>
                        </button>

                        {/* Tick to add this token to a combined bill. Separate
                            from the card itself so browsing tokens never
                            changes what is about to be charged. */}
                        <button
                          onClick={() => toggleTokenSelection(group.tokenNumber)}
                          aria-pressed={isSelected}
                          aria-label={
                            isSelected
                              ? t('Remove token {n} from the combined bill', { n: group.tokenNumber })
                              : t('Add token {n} to the combined bill', { n: group.tokenNumber })
                          }
                          title={t('Pay together with other tokens')}
                          className={`px-2 flex items-center border-l ${
                            isOpen
                              ? 'border-cafe-500 text-white hover:bg-cafe-700'
                              : 'border-gray-200 text-gray-400 hover:text-cafe-600 hover:bg-cafe-50'
                          }`}
                        >
                          {isSelected ? (
                            <CheckSquare className={`h-4 w-4 ${isOpen ? '' : 'text-cafe-600'}`} />
                          ) : (
                            <Square className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Several tokens ticked - one payment covers them all.
                  Fixed rather than sticky: a sticky element is only pinned
                  while its own container is on screen, so scrolling past the
                  token list used to carry this bar up the page with it. Fixed
                  keeps it sitting just above the tab bar wherever you are.
                  The spacer below gives back the height it no longer takes in
                  flow, so the last token is not left underneath it. */}
              {selectedGroups.length > 0 && (
                <>
                <div className="fixed inset-x-4 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] md:inset-x-0 md:bottom-4 md:mx-auto md:max-w-2xl md:px-4 z-40 bg-white rounded-xl border-2 border-cafe-500 shadow-lg p-3 flex items-center gap-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-500 truncate">
                      {selectedGroups.length}{' '}
                      {t(selectedGroups.length === 1 ? 'token' : 'tokens')} ·{' '}
                      {t('{count} items', {
                        count: selectedGroups.reduce((sum, group) => sum + group.orders.length, 0)
                      })}
                    </p>
                    <p className="text-lg font-bold text-gray-900 leading-tight">
                      Rs {selectedTotal.toFixed(2)}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedTokens([])}
                    className="px-3 py-2.5 min-h-[44px] text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                  >
                    {t('Clear')}
                  </button>
                  <button
                    onClick={() => setPayingSelection(true)}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex-shrink-0"
                  >
                    <CreditCard className="h-4 w-4" />
                    <span className="whitespace-nowrap">{t('Pay together')}</span>
                  </button>
                </div>
                <div aria-hidden="true" className="h-20" />
                </>
              )}

              {openGroup && (
                <div
                  key={openGroup.tokenNumber}
                  className={`bg-white rounded-xl shadow-sm border-2 overflow-hidden ${
                    openGroup.tokenNumber === currentToken ? 'border-cafe-500' : 'border-gray-200'
                  }`}
                >
                  {/* Token header */}
                  <div className="flex items-center justify-between gap-3 px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                      <span className="inline-flex items-center justify-center h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-cafe-600 text-white font-bold flex-shrink-0">
                        {openGroup.tokenNumber || '?'}
                      </span>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 leading-tight">
                          {t('Token #{n}', { n: openGroup.tokenNumber })}
                          {openGroup.tokenNumber === currentToken && (
                            <span className="ml-2 text-xs font-medium text-cafe-600">{t('in progress')}</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatWaitTime(openGroup.startedAt)}
                          <span className="text-gray-300">·</span>
                          {openGroup.orders.length} {t(openGroup.orders.length === 1 ? 'item' : 'items')}
                        </p>
                      </div>
                    </div>

                    <p className="text-lg font-bold text-cafe-600 flex-shrink-0">
                      Rs {openGroup.total.toFixed(2)}
                    </p>
                  </div>

                  {/* Lines */}
                  <div className="divide-y divide-gray-100">
                    {openGroup.orders.map((order) => (
                      <div key={order._id} className="flex items-center gap-2.5 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3">
                        {order.item?.image ? (
                          <img
                            src={getItemImageSrc(order.item.image)}
                            alt={orderLabel(order, language)}
                            className="w-10 h-10 sm:w-12 sm:h-12 object-cover rounded-lg border border-gray-200 flex-shrink-0"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <ShoppingBag className="h-5 w-5 text-gray-400" />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate leading-tight">
                            {orderLabel(order, language)}
                            {order.quantity > 1 && (
                              <span className="text-cafe-600"> × {order.quantity}</span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500">
                            {t('Rs {amount} each', {
                              amount: (order.unitPrice || order.itemPrice || 0).toFixed(2)
                            })}
                          </p>
                        </div>

                        <span className="font-semibold text-gray-900 flex-shrink-0">
                          Rs {(order.totalAmount || 0).toFixed(2)}
                        </span>

                        <button
                          onClick={() => handleRemoveOrder(order)}
                          className="p-2 -mr-1 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                          title={t('Remove this item')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Add more, print the bill, or settle it. Side by side even
                      on a phone - payment takes the width, the other two stay a
                      thumb away and drop to icons where there is no room. */}
                  <div className="px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 border-t border-gray-200 flex gap-2">
                    {/* The token is already on screen, so billing it should not
                        mean typing its number back in */}
                    <button
                      onClick={() => openBill(openGroup.tokenNumber)}
                      aria-label={t('Print or share the bill for token {n}', { n: openGroup.tokenNumber })}
                      title={t('Print or share this bill')}
                      className="flex-shrink-0 flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 min-h-[44px] bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap"
                    >
                      <Printer className="h-4 w-4 flex-shrink-0" />
                      <span className="hidden sm:inline">{t('Bill')}</span>
                    </button>
                    <button
                      onClick={() => handleAddToToken(openGroup.tokenNumber)}
                      className="flex-shrink-0 sm:flex-1 flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 min-h-[44px] bg-white border border-cafe-600 text-cafe-700 rounded-lg hover:bg-cafe-50 transition-colors font-medium whitespace-nowrap"
                    >
                      <Plus className="h-4 w-4 flex-shrink-0" />
                      {/* Two whole phrases rather than "Add" plus a tacked-on
                          " Items": word order is not the same in every language,
                          so a sentence split across elements cannot be translated. */}
                      <span className="sm:hidden">{t('Add')}</span>
                      <span className="hidden sm:inline">{t('Add Items')}</span>
                    </button>
                    <button
                      onClick={() => setPayingToken(openGroup.tokenNumber)}
                      className="flex-1 sm:flex-[2] flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 min-h-[44px] bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium whitespace-nowrap"
                    >
                      <CreditCard className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">
                        <span className="sm:hidden">{t('Pay')}</span>
                        <span className="hidden sm:inline">{t('Take Payment -')}</span>
                        {' '}
                        Rs {openGroup.total.toFixed(2)}
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {billOpen && (
        <BillModal
          tokenNumber={billToken}
          onClose={() => {
            setBillOpen(false);
            setBillToken(null);
          }}
        />
      )}

      {/* Payment Modal - one token, or several settled as one bill */}
      {paying && (
        <PaymentModal
          isBulk
          orders={paying.orders}
          label={paying.label}
          onComplete={handleCompletePayment}
          onClose={() => {
            setPayingToken(null);
            setPayingSelection(false);
          }}
        />
      )}
    </div>
  );
}

export default Orders;
